import type { Theme, Components } from "@mui/material/styles";
import { ErrorIcon, InfoIcon, SuccessIcon, WarningIcon } from "./CustomIcons";

// ----------------------------------------------------------------------

type AlertColor = "info" | "success" | "warning" | "error";

export default function Alert(theme: Theme): Components<Theme> {
  const isLight = theme.palette.mode === "light";

  const standardStyle = (color: AlertColor) => ({
    color: theme.palette[color][isLight ? "darker" : "lighter"],
    backgroundColor: theme.palette[color][isLight ? "lighter" : "darker"],
    "& .MuiAlert-icon": {
      color: theme.palette[color][isLight ? "main" : "light"],
    },
  });

  const filledStyle = (color: AlertColor) => ({
    color: theme.palette[color].contrastText,
  });

  const outlinedStyle = (color: AlertColor) => ({
    color: theme.palette[color][isLight ? "darker" : "lighter"],
    border: `solid 1px ${theme.palette[color][isLight ? "light" : "dark"]}`,
    backgroundColor: theme.palette[color][isLight ? "lighter" : "darker"],
    "& .MuiAlert-icon": {
      color: theme.palette[color][isLight ? "main" : "light"],
    },
  });

  const alertColors: AlertColor[] = ["info", "success", "warning", "error"];

  return {
    MuiAlert: {
      defaultProps: {
        iconMapping: {
          info: <InfoIcon />,
          success: <SuccessIcon />,
          warning: <WarningIcon />,
          error: <ErrorIcon />,
        },
      },

      styleOverrides: {
        message: {
          "& .MuiAlertTitle-root": {
            marginBottom: theme.spacing(0.5),
          },
        },
        action: {
          "& button:not(:first-of-type)": {
            marginLeft: theme.spacing(1),
          },
        },
        root: {
          variants: [
            ...alertColors.map((color) => ({
              props: { variant: "standard" as const, colorSeverity: color },
              style: standardStyle(color),
            })),
            ...alertColors.map((color) => ({
              props: { variant: "filled" as const, colorSeverity: color },
              style: filledStyle(color),
            })),
            ...alertColors.map((color) => ({
              props: { variant: "outlined" as const, colorSeverity: color },
              style: outlinedStyle(color),
            })),
          ],
        },
      },
    },
  };
}
